/**
 * Landing Page for Indian Street Food & Co
 * Management System focused on modules and functionalities
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, Menu, X, ChevronRight,
  Instagram, Facebook,
  // Module Icons
  LayoutDashboard, ShoppingCart, CalendarDays, Users,
  Utensils, Package, DollarSign, BarChart3,
  Building2, Truck, Settings, Shield,
  CreditCard, HeartHandshake, Brain, Globe,
  Handshake, Store, UserPlus, FileCheck
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const coreModules = [
    {
      icon: ShoppingCart,
      title: "Point of Sale",
      description: "Full-featured POS with floor plans, table management, split bills, and Stripe payments",
      features: ["Table Management", "Order Types", "Stripe Integration", "Kitchen Display"],
      status: "live",
    },
    {
      icon: CalendarDays,
      title: "Reservations",
      description: "Smart booking system with capacity planning, waitlists, and table optimization",
      features: ["Online Booking", "Capacity Planning", "SMS Reminders", "Overrun Alerts"],
      status: "live",
    },
    {
      icon: Utensils,
      title: "Menu Management",
      description: "Dynamic menu builder with modifiers, allergens, pricing rules, and multi-language",
      features: ["Item Builder", "Modifiers", "Allergen Tags", "Time-based Pricing"],
      status: "live",
    },
    {
      icon: Package,
      title: "Inventory",
      description: "Stock control with supplier management, wastage tracking, and auto-reordering",
      features: ["Stock Levels", "Supplier Links", "Wastage Log", "Low Stock Alerts"],
      status: "live",
    },
  ];

  const financeModules = [
    {
      icon: DollarSign,
      title: "Finance & VAT",
      description: "Multi-country VAT compliance with automated reporting and channel-aware tax calculation",
      features: ["VAT Reports", "P&L Statements", "Expense Tracking", "Budget Variance"],
      status: "live",
    },
    {
      icon: BarChart3,
      title: "Analytics",
      description: "Real-time dashboards with outlet comparison, sales forecasting, and AI insights",
      features: ["Live Dashboards", "Outlet Comparison", "Trend Analysis", "Custom Reports"],
      status: "live",
    },
    {
      icon: CreditCard,
      title: "Payments",
      description: "Secure Stripe Checkout integration with transaction tracking and reconciliation",
      features: ["Stripe Checkout", "Refunds", "Transaction History", "Webhooks"],
      status: "live",
    },
  ];

  const operationsModules = [
    {
      icon: Users,
      title: "Staff & Workforce",
      description: "Planday integration for scheduling, labor costs, and shift management",
      features: ["Shift Schedules", "Labor Costs", "Department View", "Overtime Tracking"],
      status: "live",
    },
    {
      icon: Truck,
      title: "Delivery Partners",
      description: "Wolt & Foodora integration with commission tracking and order aggregation",
      features: ["Order Sync", "Commission Reports", "Platform Comparison", "Net Revenue"],
      status: "live",
    },
    {
      icon: HeartHandshake,
      title: "Customer & Loyalty",
      description: "CRM with loyalty points, tier system, customer preferences, and visit history",
      features: ["Customer Database", "Loyalty Points", "Tier System", "Dietary Prefs"],
      status: "live",
    },
  ];

  const enterpriseModules = [
    {
      icon: Handshake,
      title: "Franchise Model",
      description: "Complete franchise lifecycle management from onboarding to operations",
      features: ["Partner Portal", "Fee Management", "Compliance", "Training"],
      status: "live",
    },
    {
      icon: Building2,
      title: "Multi-Outlet",
      description: "Manage multiple locations with centralized control and outlet-specific settings",
      features: ["8 Outlets Live", "Role-Based Access", "Cross-Outlet Reports"],
      status: "live",
    },
    {
      icon: Globe,
      title: "Multi-Country",
      description: "Norway & Sweden support with localized VAT, currencies, and language",
      features: ["NO/SE Support", "Local VAT Rules", "Multi-Currency"],
      status: "live",
    },
    {
      icon: Brain,
      title: "AI Assistant",
      description: "Sales forecasting, demand prediction, anomaly detection, and recommendations",
      features: ["Sales Forecast", "Demand Prediction", "Anomaly Alerts"],
      status: "beta",
    },
    {
      icon: Shield,
      title: "Security",
      description: "Enterprise-grade security with role-based permissions and audit logging",
      features: ["JWT Auth", "RBAC", "Audit Logs"],
      status: "live",
    },
  ];

  const stats = [
    { value: "8", label: "Outlets Live" },
    { value: "2", label: "Countries" },
    { value: "15+", label: "Modules" },
    { value: "99.9%", label: "Uptime" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img 
                src="/logo-isf.png"
                alt="Indian Street Food & Co"
                className="h-12 object-contain"
              />
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#modules" className="text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider">Modules</a>
              <a href="#features" className="text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider">Features</a>
              <a href="#enterprise" className="text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider">Enterprise</a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Button 
                data-testid="staff-login-btn"
                className="bg-[#c4a35a] hover:bg-[#d4b36a] text-black font-medium px-6"
                onClick={() => navigate('/login')}
              >
                Staff Login
              </Button>
            </div>

            <button 
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a] border-t border-white/10 py-4 px-4">
            <div className="flex flex-col gap-4">
              <a href="#modules" className="text-white/70 hover:text-white py-2 text-sm uppercase tracking-wider">Modules</a>
              <a href="#features" className="text-white/70 hover:text-white py-2 text-sm uppercase tracking-wider">Features</a>
              <a href="#enterprise" className="text-white/70 hover:text-white py-2 text-sm uppercase tracking-wider">Enterprise</a>
              <hr className="border-white/10" />
              <Button 
                className="bg-[#c4a35a] hover:bg-[#d4b36a] text-black"
                onClick={() => navigate('/login')}
              >
                Staff Login
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-[#c4a35a]/5 via-transparent to-transparent" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#c4a35a]/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-[#c4a35a]/20 text-[#c4a35a] border-[#c4a35a]/30 mb-6">
                Restaurant Management System
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-6 leading-[1.1]">
                One platform to manage{' '}
                <span className="text-[#c4a35a]">everything</span>
              </h1>

              <p className="text-lg text-white/70 mb-10 max-w-xl font-light leading-relaxed">
                Enterprise-grade restaurant management powering Indian Street Food & Co 
                across Norway and Sweden. POS, reservations, inventory, finance, and more.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-10">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-light text-[#c4a35a]">{stat.value}</div>
                    <div className="text-xs text-white/50 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-[#c4a35a] hover:bg-[#d4b36a] text-black font-medium px-8"
                  onClick={() => navigate('/login')}
                >
                  Access Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 hover:border-white/60 text-white"
                  onClick={() => document.getElementById('modules').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Modules
                </Button>
              </div>
            </div>

            {/* Dashboard Preview */}
            <div className="hidden lg:block relative">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" />
                  <span className="text-xs text-white/40 ml-2">Dashboard Preview</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Today's Revenue", value: "NOK 48,250" },
                    { label: "Orders", value: "127" },
                    { label: "Reservations", value: "42" },
                    { label: "Active Tables", value: "18/24" },
                    { label: "Avg Wait Time", value: "12 min" },
                    { label: "Staff On-Duty", value: "8" },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/5 rounded-lg p-3">
                      <div className="text-[10px] text-white/40 uppercase">{item.label}</div>
                      <div className="text-lg font-medium text-white">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-48 h-48 border border-[#c4a35a]/20 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Franchise Onboarding Highlight */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#c4a35a]/10 via-[#111] to-[#c4a35a]/10 border-y border-[#c4a35a]/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#c4a35a]/20 text-[#c4a35a] border-[#c4a35a]/30 mb-4">
                <Handshake className="w-3 h-3 mr-1" />
                Franchise Ready
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-light mb-4 text-white">
                Franchise Onboarding
              </h2>
              <p className="text-white/70 text-lg mb-6 leading-relaxed">
                Streamlined onboarding system for new franchise partners. From application to 
                launch, manage the entire franchise lifecycle with automated workflows, 
                compliance checks, and training modules.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {[
                  { icon: UserPlus, label: "Partner Applications", desc: "Digital application & vetting" },
                  { icon: FileCheck, label: "Compliance Tracking", desc: "Document verification & approvals" },
                  { icon: Store, label: "Outlet Setup", desc: "Location config & menu setup" },
                  { icon: Settings, label: "System Training", desc: "Staff onboarding & certification" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                    <div className="w-8 h-8 rounded bg-[#c4a35a]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#c4a35a]" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white">{item.label}</div>
                      <div className="text-xs text-white/50">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline"
                className="border-[#c4a35a]/50 text-[#c4a35a] hover:bg-[#c4a35a]/10"
                onClick={() => navigate('/login')}
              >
                Access Franchise Portal
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Franchise Stats */}
            <div className="hidden lg:block">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#c4a35a]" />
                  Franchise Network
                </h3>
                <div className="space-y-4">
                  {[
                    { label: "Corporate Outlets", value: "4", country: "Norway" },
                    { label: "Franchise Outlets", value: "4", country: "Sweden" },
                    { label: "Pending Applications", value: "2", country: "Denmark" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <div className="text-sm text-white">{item.label}</div>
                        <div className="text-xs text-white/50">{item.country}</div>
                      </div>
                      <div className="text-2xl font-light text-[#c4a35a]">{item.value}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/60">Total Network Value</span>
                    <span className="text-[#c4a35a] font-medium">NOK 12.5M Monthly</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Modules Section */}
      <section id="modules" className="py-24 px-4 bg-[#111]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#c4a35a] text-sm uppercase tracking-[0.3em] mb-4">Core Modules</p>
            <h2 className="text-4xl sm:text-5xl font-light mb-4 text-white">
              Daily Operations
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Everything you need to run your restaurant floor efficiently
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {coreModules.map((module, index) => (
              <Card 
                key={index} 
                className="bg-white/5 border-white/10 hover:border-[#c4a35a]/30 transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#c4a35a]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#c4a35a]/20 transition-colors">
                      <module.icon className="w-6 h-6 text-[#c4a35a]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-medium text-white">{module.title}</h3>
                        <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400">
                          {module.status}
                        </Badge>
                      </div>
                      <p className="text-white/60 text-sm mb-4">{module.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {module.features.map((feature, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-white/5 rounded text-white/50">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Finance Modules Section */}
      <section id="features" className="py-24 px-4 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#c4a35a] text-sm uppercase tracking-[0.3em] mb-4">Finance & Analytics</p>
            <h2 className="text-4xl sm:text-5xl font-light mb-4 text-white">
              Financial Control
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Complete visibility into your business performance with multi-country compliance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {financeModules.map((module, index) => (
              <Card 
                key={index} 
                className="bg-white/5 border-white/10 hover:border-[#c4a35a]/30 transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#c4a35a]/10 flex items-center justify-center mb-4 group-hover:bg-[#c4a35a]/20 transition-colors">
                    <module.icon className="w-6 h-6 text-[#c4a35a]" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-medium text-white">{module.title}</h3>
                    <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400">
                      {module.status}
                    </Badge>
                  </div>
                  <p className="text-white/60 text-sm mb-4">{module.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {module.features.map((feature, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-white/5 rounded text-white/50">
                        {feature}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Operations Modules */}
      <section className="py-24 px-4 bg-[#111]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#c4a35a] text-sm uppercase tracking-[0.3em] mb-4">Operations</p>
            <h2 className="text-4xl sm:text-5xl font-light mb-4 text-white">
              Staff & Customers
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Workforce management, delivery partners, and customer relationships
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {operationsModules.map((module, index) => (
              <Card 
                key={index} 
                className="bg-white/5 border-white/10 hover:border-[#c4a35a]/30 transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#c4a35a]/10 flex items-center justify-center mb-4 group-hover:bg-[#c4a35a]/20 transition-colors">
                    <module.icon className="w-6 h-6 text-[#c4a35a]" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-medium text-white">{module.title}</h3>
                    <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400">
                      {module.status}
                    </Badge>
                  </div>
                  <p className="text-white/60 text-sm mb-4">{module.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {module.features.map((feature, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-white/5 rounded text-white/50">
                        {feature}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section id="enterprise" className="py-24 px-4 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#c4a35a] text-sm uppercase tracking-[0.3em] mb-4">Enterprise Grade</p>
            <h2 className="text-4xl sm:text-5xl font-light mb-4 text-white">
              Built to Scale
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Multi-outlet, multi-country architecture ready for franchise expansion
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {enterpriseModules.map((module, index) => (
              <Card 
                key={index} 
                className="bg-white/5 border-white/10 hover:border-[#c4a35a]/30 transition-all duration-300 group"
              >
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-[#c4a35a]/10 flex items-center justify-center mb-3 group-hover:bg-[#c4a35a]/20 transition-colors">
                    <module.icon className="w-5 h-5 text-[#c4a35a]" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-medium text-white">{module.title}</h3>
                    <Badge 
                      variant="outline" 
                      className={`text-[9px] ${module.status === 'beta' ? 'border-yellow-500/30 text-yellow-400' : 'border-green-500/30 text-green-400'}`}
                    >
                      {module.status}
                    </Badge>
                  </div>
                  <p className="text-white/60 text-xs mb-3">{module.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {module.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-white/5 rounded text-white/40">
                        {feature}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-[#111] to-[#0a0a0a]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-light mb-6 text-white">
            Ready to get started?
          </h2>
          <p className="text-white/60 text-lg mb-10">
            Access the full management dashboard with your staff credentials
          </p>
          <Button 
            size="lg"
            className="bg-[#c4a35a] hover:bg-[#d4b36a] text-black font-medium px-10 py-6 text-lg"
            onClick={() => navigate('/login')}
          >
            Access Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/logo-isf.png"
                alt="Indian Street Food & Co"
                className="h-10 mb-4"
              />
              <p className="text-white/50 text-sm">
                Enterprise restaurant management for the modern dining experience.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-3 text-white/80">Core</h4>
              <ul className="space-y-2 text-white/50 text-sm">
                <li>Point of Sale</li>
                <li>Reservations</li>
                <li>Menu Management</li>
                <li>Inventory</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-3 text-white/80">Finance</h4>
              <ul className="space-y-2 text-white/50 text-sm">
                <li>VAT & Compliance</li>
                <li>Analytics</li>
                <li>Payments</li>
                <li>Reports</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-3 text-white/80">Connect</h4>
              <div className="flex gap-3">
                <a 
                  href="https://www.instagram.com/indianstreetfoodcompany/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#c4a35a] hover:text-[#c4a35a] transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a 
                  href="https://www.facebook.com/indianstreetfoodsweden"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#c4a35a] hover:text-[#c4a35a] transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Indian Street Food & Co. All rights reserved.
            </p>
            <p className="text-white/40 text-sm">
              Norway • Sweden • 8 Outlets
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
